import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { ConfigService } from "./config";
import { AuthService } from "./auth-service";
import { TranslocoService } from "@jsverse/transloco";

@Injectable({
  providedIn: "root",
})
export abstract class BackendService {
  protected _config = inject(ConfigService);
  protected _authService = inject(AuthService);
  private readonly _httpBackend = inject(HttpBackend);

  private readonly _http: HttpClient = new HttpClient(this._httpBackend);
  private readonly _translocoService = inject(TranslocoService);

  private readonly _baseURL: string = this._config.get("BACKEND_URI");

  // Generic GET that automatically includes the bearer token
  protected async get<T>(endpoint: string, params?: HttpParams): Promise<T> {
    const token = await firstValueFrom(
      this._authService.getAccessToken(this._config.get("SCOPE_URI"))
    );
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'X-Culture': this._translocoService.getActiveLang()
    });
    return firstValueFrom(
      this._http.get<T>(`${this._baseURL}/${endpoint}`, { params, headers })
    );
  }

  protected async downloadBlob(endpoint: string, params?: HttpParams): Promise<HttpResponse<Blob>> {
    const token = await firstValueFrom(
      this._authService.getAccessToken(this._config.get("SCOPE_URI"))
    );
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return firstValueFrom(
      this._http.get(`${this._baseURL}/${endpoint}`, { params, headers, observe: 'response', responseType: 'blob' })
    );
  }

  protected async post<T, R>(
    endpoint: string,
    body: T,
    params?: HttpParams
  ): Promise<R> {
    const token = await firstValueFrom(
      this._authService.getAccessToken(this._config.get("SCOPE_URI"))
    );

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'X-Culture': this._translocoService.getActiveLang()
    });

    return firstValueFrom(
      this._http.post<R>(`${this._baseURL}/${endpoint}`, body, {
        params,
        headers,
      })
    );
  }

  protected async delete<T>(endpoint: string, params?: HttpParams, body?: string[]): Promise<T> {
    const token = await firstValueFrom(
      this._authService.getAccessToken(this._config.get("SCOPE_URI"))
    );
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return firstValueFrom(
      this._http.delete<T>(`${this._baseURL}/${endpoint}`, { params, headers, body })
    );
  }
}
